/**
 * Number Line Constants
 *
 * Single source of truth for all number line values: styles, labels,
 * highlight colors, layout geometry, and float precision.
 * Use these constants instead of string literals or magic numbers.
 *
 * @example
 * ```typescript
 * import { NUMBER_LINE_STYLES, INTERNAL_PRECISION } from './constants/number-line-constants';
 * ```
 */

export const NUMBER_LINE_STYLES = ['arrow-right', 'arrow-left', 'arrow-both', 'line'] as const;

export type NumberLineStyle = typeof NUMBER_LINE_STYLES[number];

/**
 * Decimal precision used for float normalization across the number-line pipeline.
 * Drives `cleanFloat`, `nlNormalize`, and `normalize` — change here to update all.
 */
export const INTERNAL_PRECISION = 9;

/** Decimal places used when displaying tick labels and rounding rendered tick values. */
export const DISPLAY_PRECISION = 2;

/**
 * Percentage reserved on each side of the number line for arrows.
 * Single source of truth shared by JS snap logic and CSS via `--nl-side-pad`.
 */
export const SIDE_PAD = 7;

/** CSS custom property name used to bridge SIDE_PAD into the stylesheet. */
export const CSS_VAR_SIDE_PAD = '--nl-side-pad';

/**
 * Label visibility modes for a number line.
 * Controls which numeric tick labels are rendered on the axis.
 * by default all labels are shown
 */
export const NUMBER_LINE_LABELS = ['all', 'extremes', 'min-only', 'max-only', 'none'] as const;

export type NumberLineLabels = typeof NUMBER_LINE_LABELS[number];

/**
 * Allowed highlight colors for number line ranges.
 * Deliberately kept to a small semantic set so renderers can map them
 * to design-token colors without handling arbitrary CSS strings.
 */
export const NUMBER_LINE_HIGHLIGHT_COLORS = ['blue', 'red', 'green', 'orange', 'purple', 'yellow'] as const;

export type NumberLineHighlightColor = typeof NUMBER_LINE_HIGHLIGHT_COLORS[number];

/**
 * Drag interaction modes for a number line.
 * - `'axis'`  : draggable tokens snapped onto the axis (FreeDragAxis)
 * - `'inputs'`: standard position input fields (default)
 */
export const NUMBER_LINE_DRAG_MODES = ['axis', 'inputs'] as const;

export type NumberLineDragMode = typeof NUMBER_LINE_DRAG_MODES[number];
